import React from 'react';

/**
 * Delivery formats and destinations.
 *
 * Previously this section hotlinked four partner logos from cdn.brandfetch.io,
 * all four of which 404'd in production — leaving "AWS Logo", "Google Cloud
 * Logo", "Snowflake Logo" and "Datadog Logo" as visible alt text. The logos are
 * gone; we have no licensed copies to self-host, so the destinations are plain
 * text. Datadog was dropped outright: it is a monitoring vendor, not a delivery
 * target, and is not listed as one in the FAQ.
 *
 * Formats and access methods below are kept in sync with the FAQ's
 * "How is data delivered to clients?" answer. If you change one, change both.
 */

/** Formats, as published in the FAQ. Parquet is the default; the rest on request. */
const FORMATS: Array<{ name: string; note: string }> = [
  { name: 'Parquet', note: 'default' },
  { name: 'CSV', note: 'on request' },
  { name: 'JSON', note: 'on request' },
  { name: 'Delta', note: 'on request' },
];

/** Access methods, as published in the FAQ. */
const ACCESS_METHODS: Array<{ name: string; note: string }> = [
  { name: 'Python SDK', note: 'import sovai as sov' },
  { name: 'AWS S3', note: 'direct bucket access' },
  { name: 'GCP Cloud Storage', note: 'direct bucket access' },
  { name: 'Snowflake', note: 'share' },
  { name: 'Databricks', note: 'tables / Delta' },
  { name: 'Cloud destination push', note: 'configured per client' },
];

export const DataFormatsSection = () => {
  return (
    <div className="w-full bg-[#0d1117] border-b border-[#1d2838] py-14 px-6 lg:py-20 lg:px-12 flex justify-center">
      <div className="w-full max-w-5xl flex flex-col gap-y-10">
        <div className="flex flex-col gap-2">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-gray-400">
            Delivery
          </div>
          <h2 className="text-2xl md:text-3xl text-white font-bold">
            Formats and destinations
          </h2>
          <p className="text-gray-400 text-sm md:text-base max-w-2xl">
            Datasets are versioned and partitioned, with column schemas in the
            data dictionary. A client-specific view can omit or mask named
            columns.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {/* Formats */}
          <section className="flex flex-col gap-4">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-blue-400">
              File formats
            </h3>
            <dl className="flex flex-col divide-y divide-[#1d2838] border-y border-[#1d2838]">
              {FORMATS.map(format => (
                <div
                  key={format.name}
                  className="flex items-baseline justify-between gap-4 py-2.5"
                >
                  <dt className="font-mono text-sm text-gray-200">
                    {format.name}
                  </dt>
                  <dd className="font-mono text-[11px] text-gray-400">
                    {format.note}
                  </dd>
                </div>
              ))}
            </dl>
            <p className="text-xs text-gray-400">
              Anything else — Excel, a SQL dump, a pandas-native path — is a
              conversion on our side, not a supported delivery format. Ask
              before you plan around it.
            </p>
          </section>

          {/* Access methods */}
          <section className="flex flex-col gap-4">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-green-400">
              Access methods
            </h3>
            <dl className="flex flex-col divide-y divide-[#1d2838] border-y border-[#1d2838]">
              {ACCESS_METHODS.map(method => (
                <div
                  key={method.name}
                  className="flex items-baseline justify-between gap-4 py-2.5"
                >
                  <dt className="font-mono text-sm text-gray-200">
                    {method.name}
                  </dt>
                  <dd className="font-mono text-[11px] text-gray-400 text-right">
                    {method.note}
                  </dd>
                </div>
              ))}
            </dl>
            <p className="text-xs text-gray-400">
              We have not published end-to-end delivery latency. When it is
              measured it will be listed here per method rather than described.
            </p>
          </section>
        </div>

        {/* Integration support — claims kept to what is actually offered. */}
        <section className="flex flex-col gap-3 border-t border-[#1d2838] pt-8">
          <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-purple-400">
            Integration support
          </h3>
          <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm text-gray-400">
            <li className="border border-[#1d2838] rounded-md p-3">
              A named technical contact for the integration.
            </li>
            <li className="border border-[#1d2838] rounded-md p-3">
              Custom delivery configuration at no additional cost.
            </li>
            <li className="border border-[#1d2838] rounded-md p-3">
              Priority support on the Corporate plan.
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
};
